import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { vendorsAPI, returnsAPI, branchesAPI, API_BASE_URL, getRawMaterials, getCompanyAccounts, purchasesAPI } from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import VendorTransactions from './VendorTransactionsTable';
import SearchableDropdown from '../components/SearchableDropdown';
// import { rawMaterialList } from '../utils/rawMaterialList';
import '../css/VendorPurchaseDetails.css';

function VendorPurchaseDetails() {
    const { vendorId } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);

    // console.log('=== VENDOR PURCHASE DETAILS DEBUG ===');
    // console.log('vendorId:', vendorId);

    // States
    const [vendor, setVendor] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [transactionForm, setTransactionForm] = useState({
        transaction_type: 'PURCHASE',
        transaction_date: new Date().toISOString().split('T')[0],
        amount: '',
        discount: '',
        with_gst: true,
        total_amount: '',
        amount_paid: '',
        account_paid: '',
        reference_number: '',
        notes: '',
        executive_name: '',
        branch_name: '',
        payment_mode: '',
        items: [
            { product: '', quantity: '', unit_price: '', amount: '', discount: '', gst_percent: 18 }
        ]
    });
    const [openingBalance, setOpeningBalance] = useState(0);
    const [currentBalance, setCurrentBalance] = useState(0);
    const [accountSummary, setAccountSummary] = useState(null);
    const [showOpeningBalanceModal, setShowOpeningBalanceModal] = useState(false);
    const [openingBalanceForm, setOpeningBalanceForm] = useState({
        opening_balance: '',
        transaction_date: new Date().toISOString().split('T')[0],
        notes: ''
    });
    const [apiTransactionCount, setApiTransactionCount] = useState(null);
    const [purchaseExecutives, setPurchaseExecutives] = useState([]); // New state
    const [rawMaterials, setRawMaterials] = useState([]); // Dynamic raw material list
    const [branches, setBranches] = useState([]); // New state for branches
    const [companyAccounts, setCompanyAccounts] = useState([]); // New state for company accounts

    // Table Filtering States
    const [tableFilterType, setTableFilterType] = useState('all');
    const [tableFromDate, setTableFromDate] = useState('');
    const [tableToDate, setTableToDate] = useState('');

    // ====================== FETCH DATA ======================
    useEffect(() => {
        if (vendorId) {
            fetchVendorData();
            fetchPurchaseExecutives(); // Fetch executives
            fetchRawMaterialsData(); // Fetch raw materials
            fetchBranches(); // Fetch branches
            fetchCompanyAccounts(); // Fetch company accounts
        } else {
            setError('No vendor ID provided');
            setLoading(false);
        }
    }, [vendorId]);

    const fetchRawMaterialsData = async () => {
        try {
            const data = await getRawMaterials();
            if (Array.isArray(data)) {
                setRawMaterials(data.map(m => m.name));
            }
        } catch (error) {
            // console.error('Error fetching raw materials:', error);
        }
    };

    const fetchPurchaseExecutives = async () => {
        try {
            const response = await purchasesAPI.getExecutiveOfficers();
            // API returns straight array: [{id, name, role, type, ...}, ...]
            const data = response.data;

            if (Array.isArray(data)) {
                const uniqueOfficers = [...new Set(data.filter(o => o.name).map(o => o.name))];
                setPurchaseExecutives(uniqueOfficers);
            } else if (data && data.success && Array.isArray(data.officers)) {
                const uniqueOfficers = [...new Set(data.officers.filter(o => o.name).map(o => o.name))];
                setPurchaseExecutives(uniqueOfficers);
            }
        } catch (error) {
            // console.error('Error fetching purchase executives:', error);
        }
    };

    const fetchBranches = async () => {
        try {
            const response = await branchesAPI.getAll();
            if (response.data && response.data.success) {
                setBranches(response.data.branches || []);
            }
        } catch (error) {
            // console.error('Error fetching branches:', error);
        }
    };

    const fetchCompanyAccounts = async () => {
        try {
            const accounts = await getCompanyAccounts();
            setCompanyAccounts(accounts || []);
        } catch (error) {
            // console.error('Error fetching company accounts:', error);
        }
    };

    const fetchVendorData = async () => {
        try {
            setLoading(true);
            setError(null);

            // console.log('Fetching vendor details for ID:', vendorId);

            // 1. Fetch vendor details using vendorsAPI
            const vendorResponse = await vendorsAPI.get(vendorId);
            // console.log('Vendor response:', vendorResponse.data);

            if (vendorResponse.data) {
                setVendor(vendorResponse.data);

                // Pre-fill executive name if available
                if (vendorResponse.data.executive_officer_name || vendorResponse.data.sales_executive || vendorResponse.data.purchase_executive) {
                    setTransactionForm(prev => ({
                        ...prev,
                        executive_name: vendorResponse.data.executive_officer_name || vendorResponse.data.sales_executive || vendorResponse.data.purchase_executive
                    }));
                }

                // 2. Fetch opening balance using vendorsAPI
                const { bal: fetchedOpeningBalance, account } = await fetchOpeningBalance();

                // 3. Fetch purchase transactions
                await fetchPurchaseTransactions(fetchedOpeningBalance, account);

                // 4. Using API count, so fetch it
                await fetchTransactionCount(vendorId);

                // 5. Calculate account summary (will happen in useEffect when transactions change)
            }
        } catch (err) {
            // console.error('Error fetching vendor data:', err);
            handleApiError(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPurchaseTransactions = async (currentOpeningBalance = openingBalance, account = null) => {
        try {
            // console.log('Fetching purchase transactions and returns for:', vendorId);

            // Parallel fetch
            const [purchasesRes, returnsRes] = await Promise.all([
                vendorsAPI.getTransactions(vendorId),
                returnsAPI.getAll({ client_id: vendorId, client_type: 'Vendor' })
            ]);

            // 1. Process Purchases
            let purchaseTransactions = [];
            if (purchasesRes.data && purchasesRes.data.success) {
                // Ensure array and filter out DB's opening balance to avoid duplication with manual row
                const rawPurchases = (purchasesRes.data.transactions || []).filter(t => (t.transaction_type || '').toLowerCase() !== 'opening');
                purchaseTransactions = rawPurchases.map(txn => {
                    const total = parseFloat(txn.total_amount || txn.amount || 0);
                    const paid = parseFloat(txn.amount_paid || 0);
                    const typeLower = (txn.transaction_type || '').toLowerCase();

                    // Gross Balance from Backend
                    let rowBalance = parseFloat(txn.balance);
                    if (isNaN(rowBalance)) {
                        if (['purchase', 'sale', 'cash', 'cheque', 'bank_transfer', 'upi'].includes(typeLower)) {
                            rowBalance = total;
                        } else if (typeLower === 'return' || typeLower === 'credit_note') {
                            rowBalance = -total;
                        } else {
                            rowBalance = 0;
                        }
                    }

                    return {
                        ...txn,
                        total_amount: total,
                        amount_paid: paid,
                        paid_amount: paid,
                        balance: rowBalance, // Gross column
                        due_balance: total - paid // Net column if used
                    };
                });
            }

            // 2. Process Returns (if any from returnsAPI)
            let returnTransactions = [];
            if (returnsRes.data) {
                const returnsData = Array.isArray(returnsRes.data) ? returnsRes.data : [];
                returnTransactions = returnsData.map((ret, index) => {
                    const refundAmount = parseFloat(ret.refund_amount || ret.total_amount || 0);
                    const amountPaid = parseFloat(ret.amount_paid || 0);

                    return {
                        id: `RET-${ret.id || index}`,
                        transaction_date: ret.date || ret.date_initiated || new Date().toISOString().split('T')[0],
                        reference_number: ret.return_number || ret.return_id || '',
                        bill_number: ret.return_number || ret.return_id || '',
                        transaction_type: 'return',
                        product: ret.product || ret.reason || 'Purchase Return',
                        quantity: ret.quantity || 0,
                        unit_price: ret.unit_price || 0,
                        purchase_price: ret.unit_price || 0,
                        discount: ret.discount || 0,
                        with_gst: Boolean(ret.with_gst),
                        total_amount: refundAmount,
                        amount_paid: amountPaid,
                        paid_amount: amountPaid,
                        balance: -refundAmount, // Returns reduce gross debt
                        due_balance: -(refundAmount - amountPaid),
                        notes: ret.notes || ''
                    };
                });
            }

            // 3. Process Opening Balance (if exists)
            let openingBalanceTransaction = [];
            if (currentOpeningBalance > 0 || currentOpeningBalance < 0) {
                const openingPaid = account?.opening_balance_paid || 0;
                openingBalanceTransaction = [{
                    id: 'opening-balance',
                    transaction_date: vendor?.created_at ? vendor.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
                    reference_number: '-',
                    bill_number: '-',
                    transaction_type: 'opening',
                    product: 'Opening Balance',
                    quantity: 0,
                    unit_price: 0,
                    purchase_price: 0,
                    discount: 0,
                    with_gst: false,
                    total_amount: currentOpeningBalance,
                    amount_paid: openingPaid,
                    paid_amount: openingPaid,
                    balance: currentOpeningBalance,
                    due_balance: currentOpeningBalance - openingPaid,
                    notes: '',
                    isOpeningBalance: true,
                    account_paid: account?.opening_balance_account_paid || ''
                }];
            }

            // 4. Combine and Sort
            let allTransactions = [...openingBalanceTransaction, ...purchaseTransactions, ...returnTransactions].sort((a, b) =>
                new Date(b.transaction_date) - new Date(a.transaction_date)
            );

            // 5. Set row-level balance to Gross Amount (Total)
            allTransactions = allTransactions.map(txn => {
                let rowBalance = 0;
                if (txn.transaction_type === 'return') {
                    rowBalance = -(parseFloat(txn.total_amount) || 0); // Credit
                } else if (txn.transaction_type === 'payment') {
                    rowBalance = 0; // Payment doesn't add to gross debt, it reduces net via amount_paid
                } else {
                    rowBalance = parseFloat(txn.total_amount) || 0; // Purchase/Opening
                }

                return {
                    ...txn,
                    balance: rowBalance
                };
            });

            setTransactions(allTransactions);

            // Update Count
            if (purchasesRes.data && purchasesRes.data.totalTransactions !== undefined) {
                setApiTransactionCount(purchasesRes.data.totalTransactions + returnTransactions.length + openingBalanceTransaction.length);
            } else {
                setApiTransactionCount(allTransactions.length);
            }

        } catch (error) {
            // console.error('Error fetching transactions:', error);
        }
    };

    const fetchTransactionCount = async (id) => {
        try {
            const res = await vendorsAPI.getTransactionCount(id);
            if (res.data.success) {
                setApiTransactionCount(res.data.count);
            }
        } catch (err) {
            // console.error('Error fetching transaction count:', err);
        }
    };

    const fetchOpeningBalance = async () => {
        try {
            console.log('Fetching opening balance for vendor:', vendorId);
            const response = await vendorsAPI.getOpeningBalance(vendorId);
            // console.log('Opening balance response:', response.data);

            if (response.data && response.data.success && response.data.account) {
                // Correct path: response.data.account.opening_balance
                const bal = parseFloat(response.data.account.opening_balance);
                const finalBal = isNaN(bal) ? 0 : bal;
                setOpeningBalance(finalBal);
                return { bal: finalBal, account: response.data.account };
            } else if (response.data && response.data.opening_balance !== undefined) {
                // Fallback for direct property
                const bal = parseFloat(response.data.opening_balance);
                const finalBal = isNaN(bal) ? 0 : bal;
                setOpeningBalance(finalBal);
                return { bal: finalBal, account: response.data };
            } else {
                // console.warn('Unexpected opening balance response structure:', response.data);
                setOpeningBalance(0);
                return { bal: 0, account: null };
            }
        } catch (error) {
            // console.error('Error fetching opening balance:', error);
            // setOpeningBalance(0); // Optional: keep previous state or set to 0
            return { bal: 0, account: null };
        }
    };

    const calculateAccountSummary = () => {
        try {
            // Calculate summary from transactions
            const totalTransactions = apiTransactionCount !== null ? apiTransactionCount : transactions.length;

            // Calculate totals based on transaction fields
            const totalPurchases = transactions.reduce((sum, t) =>
                sum + (parseFloat(t.total_amount || t.amount || 0)), 0);

            const totalPaid = transactions.reduce((sum, t) =>
                sum + (parseFloat(t.amount_paid || 0)), 0);

            // Calculate outstanding based on individual transaction balances to match the table
            // Table calculates balance as: (total_amount - amount_paid) for each transaction
            // User Request: Current Balance = Sum(Balance Col) - Sum(Paid Col)
            const totalGrossBalance = transactions.reduce((sum, t) => sum + (parseFloat(t.balance) || 0), 0);
            const totalAmountPaid = transactions.reduce((sum, t) => sum + (parseFloat(t.amount_paid) || 0), 0);

            const currentBalance = totalGrossBalance - totalAmountPaid;
            setCurrentBalance(currentBalance);

            // Get last transaction date
            const lastTransaction = transactions.length > 0
                ? transactions.reduce((latest, current) =>
                    new Date(current.transaction_date || current.created_at) >
                        new Date(latest.transaction_date || latest.created_at) ? current : latest)
                : null;

            setAccountSummary({
                statistics: {
                    total_transactions: totalTransactions,
                    total_purchases: totalPurchases,
                    total_paid: totalPaid,
                    total_outstanding: currentBalance,
                    total_amount_paid: totalAmountPaid,
                    last_transaction_date: lastTransaction?.transaction_date || lastTransaction?.created_at || null
                }
            });
        } catch (error) {
            // console.error('Error calculating summary:', error);
        }
    };

    useEffect(() => {
        calculateAccountSummary();
    }, [transactions, openingBalance, apiTransactionCount]);

    useEffect(() => {
        if (showTransactionModal) {
            setTransactionForm(prev => ({
                ...prev,
                transaction_type: 'PURCHASE',
                transaction_date: new Date().toISOString().split('T')[0],
                items: [
                    { product: '', quantity: '', unit_price: '', amount: '', discount: '', gst_percent: 18 }
                ],
                total_amount: '',
                amount_paid: '',
                account_paid: '',
                reference_number: '',
                notes: '',
                executive_name: vendor?.executive_officer_name || vendor?.sales_executive || vendor?.purchase_executive || '',
                branch_name: '',
                payment_mode: ''
            }));
        }
    }, [showTransactionModal, vendor]);

    const handleApiError = (err) => {
        let errorMessage = 'Failed to load vendor data';

        if (err.message === 'Network Error' || err.message.includes('Failed to fetch')) {
            errorMessage = 'Cannot connect to backend server. Please check:';
            errorMessage += `\n1. Is backend running on ${API_BASE_URL.replace('/api', '')}?`;
            errorMessage += '\n2. Check backend console for errors';
        } else if (err.response) {
            // console.error('API Error Response:', err.response);

            if (err.response.status === 400) {
                errorMessage = `Bad request: ${err.response.data?.message || 'Invalid data'}`;
            } else if (err.response.status === 404) {
                errorMessage = `Vendor not found with ID: ${vendorId}`;
                errorMessage += '\n\nPlease check:';
                errorMessage += '\n1. The vendor exists in the database';
                errorMessage += `\n2. API endpoint: GET ${API_BASE_URL}/vendors/${vendorId}`;
            } else if (err.response.status === 500) {
                errorMessage = 'Server error. Check backend logs.';
            } else {
                errorMessage = `Server returned ${err.response.status}: ${err.response.statusText}`;
            }
        } else if (err.request) {
            errorMessage = 'No response from server. Backend might be down.';
            errorMessage += '\n\nCheck these in your browser:';
            errorMessage += `\n• ${API_BASE_URL.replace('/api', '')}/`;
            errorMessage += `\n• ${API_BASE_URL}/vendors/${vendorId}`;
        }

        setError(errorMessage);
    };

    // ====================== TRANSACTION HANDLING ======================
    const handleSaveTransaction = async () => {
        try {
            if (!transactionForm.transaction_type) {
                alert('Please select a transaction type');
                return;
            }

            if (!transactionForm.transaction_date) {
                alert('Please select a transaction date');
                return;
            }

            const isPayment = (transactionForm.transaction_type || '').toLowerCase() === 'payment';

            const transactionPayload = {
                client_id: vendorId, // Required by your vendorsAPI.addTransaction
                transaction_type: transactionForm.transaction_type,
                product: !isPayment && transactionForm.items.length > 0 ? transactionForm.items[0].product : (isPayment ? 'Payment Made' : ''),
                quantity: isPayment ? 0 : transactionForm.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
                unit_price: 0,
                amount: isPayment ? 0 : Number(transactionForm.amount || 0),
                discount: isPayment ? 0 : Number(transactionForm.discount || 0),
                with_gst: isPayment ? false : transactionForm.items.some(i => Number(i.gst_percent || 0) > 0),
                total_amount: isPayment ? 0 : Number(transactionForm.total_amount || 0),
                amount_paid: Number(transactionForm.amount_paid || 0),
                account_paid: transactionForm.account_paid || '',
                transaction_date: transactionForm.transaction_date,
                reference_number: transactionForm.reference_number || '',
                notes: transactionForm.notes || '',
                executive_name: transactionForm.executive_name || '', // Send executive name
                branch_name: transactionForm.branch_name || '', // Send branch name
                items: isPayment ? [] : transactionForm.items,
                payment_mode: transactionForm.payment_mode || ''
            };

            // console.log('Sending purchase transaction:', transactionPayload);

            // Use vendorsAPI.addTransaction as defined in your API file
            await vendorsAPI.addTransaction(transactionPayload);

            // Reset and close modal
            setShowTransactionModal(false);
            resetTransactionForm();

            // Refresh data
            await fetchVendorData();

            alert('Purchase transaction saved successfully!');
        } catch (err) {
            // console.error('Failed to save transaction:', err);
            alert(`Failed to save transaction: ${err.message || 'Check backend connection'}`);
        }
    };

    const handleSaveOpeningBalance = async () => {
        try {
            if (openingBalanceForm.opening_balance === undefined) {
                alert('Please enter an opening balance');
                return;
            }

            const payload = {
                opening_balance: Number(openingBalanceForm.opening_balance),
                transaction_date: openingBalanceForm.transaction_date,
                notes: openingBalanceForm.notes
            };

            // console.log('Saving opening balance:', payload);

            // Use vendorsAPI.setOpeningBalance
            await vendorsAPI.setOpeningBalance(vendorId, payload);

            // Close modal and refresh data
            setShowOpeningBalanceModal(false);
            setOpeningBalanceForm({
                opening_balance: '',
                transaction_date: new Date().toISOString().split('T')[0],
                notes: ''
            });

            // Refresh opening balance
            await fetchOpeningBalance();

            alert('Opening balance saved successfully!');
        } catch (err) {
            // console.error('Failed to save opening balance:', err);
            alert(`Failed to save opening balance: ${err.message || 'Check backend connection'}`);
        }
    };

    const resetTransactionForm = () => {
        setTransactionForm({
            transaction_type: 'PURCHASE',
            transaction_date: new Date().toISOString().split('T')[0],
            amount: '',
            discount: '',
            with_gst: true,
            total_amount: '',
            amount_paid: '',
            account_paid: '',
            reference_number: '',
            notes: '',
            executive_name: vendor?.executive_officer_name || vendor?.sales_executive || vendor?.purchase_executive || '',
            branch_name: '',
            payment_mode: '',
            items: [
                { product: '', quantity: '', unit_price: '', amount: '', discount: '', gst_percent: 18 }
            ]
        });
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...transactionForm.items];
        newItems[index][field] = value;

        // Auto-calculate amount for the row
        if (field === 'quantity' || field === 'unit_price') {
            const qty = Number(newItems[index].quantity || 0);
            const price = Number(newItems[index].unit_price || 0);
            newItems[index].amount = qty * price;
        }

        setTransactionForm(prev => {
            const updatedForm = { ...prev, items: newItems };
            return recalculateTotal(updatedForm);
        });
    };

    const addItem = () => {
        // New items inherit the GST % from the last item (or default 18)
        const lastItem = transactionForm.items[transactionForm.items.length - 1];
        const inheritGst = lastItem ? lastItem.gst_percent : 18;
        setTransactionForm(prev => ({
            ...prev,
            items: [
                ...prev.items,
                { product: '', quantity: '', unit_price: '', amount: '', discount: '', gst_percent: inheritGst }
            ]
        }));
    };

    const removeItem = (index) => {
        if (transactionForm.items.length > 1) {
            const newItems = transactionForm.items.filter((_, i) => i !== index);
            setTransactionForm(prev => {
                const updatedForm = { ...prev, items: newItems };
                return recalculateTotal(updatedForm);
            });
        }
    };

    const recalculateTotal = (nextForm) => {
        const form = { ...nextForm };

        if ((form.transaction_type || '').toLowerCase() === 'payment') {
            form.amount = 0;
            form.discount = 0;
            form.total_amount = 0;
            return form;
        }

        let grandTotal = 0;
        let totalSubtotal = 0;
        let totalDiscount = 0;

        if (form.items && form.items.length > 0) {
            form.items.forEach(item => {
                const qty = Number(item.quantity || 0);
                const price = Number(item.unit_price || 0);
                const discount = Number(item.discount || 0);

                const subtotal = qty * price;
                totalSubtotal += subtotal;
                totalDiscount += discount;

                let itemTotal = subtotal - discount;
                if (itemTotal < 0) itemTotal = 0;

                // Apply manually entered GST percentage
                const gstPercent = Number(item.gst_percent || 0);
                if (gstPercent > 0) {
                    itemTotal = itemTotal * (1 + gstPercent / 100);
                }

                grandTotal += itemTotal;
            });

            form.amount = Number(totalSubtotal.toFixed(2));
            form.discount = Number(totalDiscount.toFixed(2));
        }

        // Apply Round-off Logic
        form.total_amount = Math.round(grandTotal);
        return form;
    };

    // ====================== FORMATTING FUNCTIONS ======================
    const formatCurrency = (amount) => {
        const numAmount = parseFloat(amount || 0);
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(numAmount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    // ====================== RENDER LOADING ======================
    if (loading) {
        return (
            <div className="account-details-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading vendor details...</p>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                        Vendor ID: <code>{vendorId}</code>
                    </p>
                </div>
            </div>
        );
    }

    // ====================== RENDER ERROR ======================
    if (error) {
        return (
            <div className="account-details-container">
                <div className="error-message">
                    <h3>⚠️ Error Loading Vendor</h3>
                    <div style={{ whiteSpace: 'pre-line', margin: '15px 0' }}>
                        {error.split('\n').map((line, index) => (
                            <p key={index} style={{ margin: '5px 0' }}>{line}</p>
                        ))}
                    </div>
                    <div className="debug-info" style={{
                        backgroundColor: '#f8f9fa',
                        padding: '15px',
                        borderRadius: '5px',
                        margin: '15px 0'
                    }}>
                        <p><strong>Debug Info:</strong></p>
                        <p>Vendor ID: <code>{vendorId}</code></p>
                        <p>Expected API: GET {API_BASE_URL}/vendors/{vendorId}</p>
                        <p>Current time: {new Date().toLocaleString()}</p>
                    </div>
                    <button
                        className="btn btn-primary"
                        onClick={fetchVendorData}
                        style={{ marginRight: '10px' }}
                    >
                        Retry Loading
                    </button>
                    <button
                        className="btn btn-secondary"
                        onClick={() => navigate('/vendors')}
                    >
                        Back to Vendors List
                    </button>
                </div>
            </div>
        );
    }

    // ====================== MAIN RENDER ======================
    return (
        <div className="account-details-container">
            {/* HEADER */}
            <div className="header-section">
                <h1>Vendor Purchase Details</h1>
                <div className="action-buttons">
                    {user?.role !== 'normal_user' && (
                        <>
                            <button
                                className="btn btn-primary"
                                onClick={() => setShowTransactionModal(true)}
                                style={{ marginRight: '10px' }}
                            >
                                + New Purchase
                            </button>
                            {/* <button
                                className="btn btn-secondary"
                                onClick={() => setShowOpeningBalanceModal(true)}
                                style={{ marginRight: '10px' }}
                            >
                                Set Opening Balance
                            </button> */}
                        </>
                    )}
                    <button
                        className="btn btn-reset"
                        onClick={() => navigate(-1)}
                    >
                        ← Back
                    </button>
                </div>
            </div>

            {/* VENDOR INFO CARD */}
            {vendor && (
                <div className="customer-info-card">
                    <div className="customer-header">
                        <h2>{vendor.name || vendor.vendor_name || 'Unnamed Vendor'}</h2>
                        <div className="account-number">
                            Vendor ID: <code>{vendor.id}</code>
                            <span className={`status-badge ${(vendor.status || 'active').toLowerCase()}`}>
                                {vendor.status || 'Active'}
                            </span>
                        </div>
                    </div>

                    <div className="customer-details-grid">
                        <div className="detail-item">
                            <label>Phone</label>
                            <span>{vendor.phone || '-'}</span>
                        </div>

                        <div className="detail-item">
                            <label>Email</label>
                            <span>{vendor.email || '-'}</span>
                        </div>

                        <div className="detail-item">
                            <label>Address</label>
                            <span>{vendor.address || '-'}</span>
                        </div>

                        <div className="detail-item">
                            <label>Contact Person</label>
                            <span>{vendor.contact_person || '-'}</span>
                        </div>

                        <div className="detail-item">
                            <label>Vendor Type</label>
                            <span>{vendor.vendor_type || vendor.client_type || '-'}</span>
                        </div>

                        <div className="detail-item">
                            <label>Executive Officer</label>
                            <span>{vendor.executive_officer_name || vendor.sales_executive || '-'}</span>
                        </div>
                    </div>

                    {/* ACCOUNT SUMMARY */}
                    <div className="account-summary-section" style={{
                        marginTop: '20px',
                        padding: '20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px'
                    }}>
                        <h3 style={{ marginBottom: '15px', color: '#333' }}>Account Summary</h3>
                        <div className="summary-grid" style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '15px'
                        }}>
                            <div className="summary-item" style={{
                                padding: '10px',
                                backgroundColor: 'white',
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    color: '#666',
                                    marginBottom: '5px'
                                }}>Opening Balance</label>
                                <span className={openingBalance >= 0 ? 'text-success' : 'text-danger'}
                                    style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                    {formatCurrency(openingBalance)}
                                </span>
                            </div>

                            <div className="summary-item" style={{
                                padding: '10px',
                                backgroundColor: 'white',
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    color: '#666',
                                    marginBottom: '5px'
                                }}>Current Balance</label>
                                <span className={currentBalance >= 0 ? 'text-success' : 'text-danger'}
                                    style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                    {formatCurrency(currentBalance)}
                                </span>
                            </div>

                            <div className="summary-item" style={{
                                padding: '10px',
                                backgroundColor: 'white',
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    color: '#666',
                                    marginBottom: '5px'
                                }}>Total Amount Paid</label>
                                <span className="text-primary" style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                    {formatCurrency(accountSummary?.statistics?.total_amount_paid || 0)}
                                </span>
                            </div>

                            <div className="summary-item" style={{
                                padding: '10px',
                                backgroundColor: 'white',
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    color: '#666',
                                    marginBottom: '5px'
                                }}>Total Transactions</label>
                                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                    {apiTransactionCount !== null ? apiTransactionCount : (accountSummary?.statistics?.total_transactions || 0)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div >
            )
            }
            <VendorTransactions
                transactions={transactions}
                vendorName={vendor?.name || vendor?.vendor_name}
                onFetch={fetchVendorData}
                filterType={tableFilterType}
                setFilterType={setTableFilterType}
                fromDate={tableFromDate}
                toDate={tableToDate}
                setFromDate={setTableFromDate}
                setToDate={setTableToDate}
            />

            {/* TRANSACTION MODAL */}
            {
                showTransactionModal && (
                    <div className="modal-overlay" style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000
                    }}>
                        <div className="modal-content" style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            width: '90%',
                            maxWidth: '1000px',
                            maxHeight: '90vh',
                            overflow: 'auto'
                        }}>
                            <div className="modal-header" style={{
                                padding: '20px',
                                borderBottom: '1px solid #e0e0e0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <h3 style={{ margin: 0 }}>Add New Purchase</h3>
                                <button
                                    className="modal-close"
                                    onClick={() => setShowTransactionModal(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '24px',
                                        cursor: 'pointer',
                                        color: '#666'
                                    }}
                                >
                                    ×
                                </button>
                            </div>

                            <div className="modal-body" style={{ padding: '20px' }}>
                                <div className="form-grid" style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                    gap: '15px'
                                }}>
                                    {/* Branch Selection */}
                                    <div className="form-group">
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '5px',
                                            fontWeight: 'bold',
                                            color: '#333'
                                        }}>Branch *</label>
                                        <select
                                            className="form-input"
                                            value={transactionForm.branch_name}
                                            onChange={e => {
                                                const selectedBranch = e.target.value;
                                                const isEstimate = selectedBranch.toLowerCase() === 'estimates';

                                                setTransactionForm(prev => {
                                                    // Automatically update all items' GST based on branch
                                                    const updatedItems = prev.items.map(item => ({
                                                        ...item,
                                                        gst_percent: isEstimate ? 0 : (item.gst_percent || 18)
                                                    }));

                                                    return recalculateTotal({
                                                        ...prev,
                                                        branch_name: selectedBranch,
                                                        items: updatedItems
                                                    });
                                                });
                                            }}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                fontSize: '14px'
                                            }}
                                        >
                                            <option value="">Select Branch</option>
                                            {branches.map((branch) => (
                                                <option key={branch.id} value={branch.name}>
                                                    {branch.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '5px',
                                            fontWeight: 'bold',
                                            color: '#333'
                                        }}>Transaction Type *</label>
                                        <select
                                            className="form-input"
                                            value={transactionForm.transaction_type}
                                            onChange={e => setTransactionForm(prev => ({
                                                ...prev,
                                                transaction_type: e.target.value
                                            }))}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                fontSize: '14px'
                                            }}
                                        >
                                            <option value="PURCHASE">Purchase Bill</option>
                                            <option value="RETURN">Return</option>
                                        </select>
                                    </div>

                                    {/* Removed Payment Mode dropdown as Payments are handled separately */}

                                    <div className="form-group">
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '5px',
                                            fontWeight: 'bold',
                                            color: '#333'
                                        }}>Date *</label>
                                        <input
                                            type="date"
                                            className="form-input"
                                            value={transactionForm.transaction_date}
                                            onChange={e => setTransactionForm(prev => ({
                                                ...prev,
                                                transaction_date: e.target.value
                                            }))}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>

                                    {/* ITEMS LIST */}
                                    {transactionForm.transaction_type !== 'payment' && (
                                        <div className="items-section" style={{ gridColumn: '1 / -1' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                <label style={{ fontWeight: 'bold' }}>Products / Items</label>
                                            </div>

                                            {/* Table Header */}
                                            <div className="items-header" style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'minmax(150px, 3fr) 80px 100px 100px 80px 40px',
                                                gap: '10px',
                                                marginBottom: '5px',
                                                padding: '0 10px',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                color: '#555'
                                            }}>
                                                <div>Product</div>
                                                <div>Qty</div>
                                                <div>Price (₹)</div>
                                                <div>Total (₹)</div>
                                                <div style={{ textAlign: 'center' }}>GST %</div>
                                                <div></div>
                                            </div>

                                            {transactionForm.items.map((item, index) => (
                                                <div key={index} className="item-row" style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'minmax(150px, 3fr) 80px 100px 100px 80px 40px',
                                                    gap: '10px',
                                                    alignItems: 'center',
                                                    marginBottom: '8px',
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #eee',
                                                    padding: '8px 10px',
                                                    borderRadius: '6px'
                                                }}>
                                                    <div style={{ marginBottom: 0 }}>
                                                        <SearchableDropdown
                                                            options={rawMaterials}
                                                            value={item.product}
                                                            onChange={val => handleItemChange(index, 'product', val)}
                                                            placeholder="Select Material"
                                                            style={{ margin: 0 }}
                                                        />
                                                    </div>
                                                    <div style={{ marginBottom: 0 }}>
                                                        <input
                                                            type="text"
                                                            className="form-input"
                                                            value={item.quantity}
                                                            onChange={e => handleItemChange(index, 'quantity', e.target.value)}
                                                            min="1"
                                                            style={{
                                                                width: '100%',
                                                                padding: '8px',
                                                                border: '1px solid #ddd',
                                                                borderRadius: '4px',
                                                                fontSize: '14px',
                                                                margin: 0
                                                            }}
                                                        />
                                                    </div>
                                                    <div style={{ marginBottom: 0 }}>
                                                        <input
                                                            type="text"
                                                            className="form-input"
                                                            value={item.unit_price}
                                                            onChange={e => handleItemChange(index, 'unit_price', e.target.value)}
                                                            min="0"
                                                            style={{
                                                                width: '100%',
                                                                padding: '8px',
                                                                border: '1px solid #ddd',
                                                                borderRadius: '4px',
                                                                fontSize: '14px',
                                                                margin: 0
                                                            }}
                                                        />
                                                    </div>
                                                    <div style={{ marginBottom: 0 }}>
                                                        <input
                                                            type="text"
                                                            className="form-input"
                                                            value={item.amount}
                                                            readOnly
                                                            style={{
                                                                width: '100%',
                                                                padding: '8px',
                                                                border: '1px solid #ddd',
                                                                borderRadius: '4px',
                                                                fontSize: '14px',
                                                                margin: 0,
                                                                backgroundColor: '#f9f9f9'
                                                            }}
                                                        />
                                                    </div>
                                                    <div style={{ marginBottom: 0 }}>
                                                        <input
                                                            type="text"
                                                            className="form-input"
                                                            value={item.gst_percent}
                                                            onChange={e => handleItemChange(index, 'gst_percent', e.target.value)}
                                                            min="0"
                                                            max="100"
                                                            step="0.5"
                                                            style={{
                                                                width: '100%',
                                                                padding: '8px',
                                                                border: '1px solid #ddd',
                                                                borderRadius: '4px',
                                                                fontSize: '14px',
                                                                margin: 0,
                                                                textAlign: 'center'
                                                            }}
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeItem(index)}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            color: '#dc3545',
                                                            cursor: 'pointer',
                                                            fontSize: '20px',
                                                            padding: '0',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        title="Remove Item"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}


                                            <button
                                                type="button"
                                                onClick={addItem}
                                                className="btn"
                                                style={{
                                                    marginTop: '5px',
                                                    marginBottom: '15px',
                                                    backgroundColor: '#e2e6ea',
                                                    color: '#495057',
                                                    padding: '6px 12px',
                                                    fontSize: '13px',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontWeight: '500'
                                                }}
                                            >
                                                + Add Another Item
                                            </button>
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '5px',
                                            fontWeight: 'bold',
                                            color: '#333'
                                        }}>Total Amount (₹)</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={formatCurrency(transactionForm.total_amount)}
                                            readOnly
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                backgroundColor: '#f8f9fa',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    </div>

                                    {/* Removed Amount Paid and Account Paid fields as Payments are now handled from Payment Management */}

                                    <div className="form-group">
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '5px',
                                            fontWeight: 'bold',
                                            color: '#333'
                                        }}>Bill Number</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={transactionForm.reference_number}
                                            onChange={e => setTransactionForm(prev => ({
                                                ...prev,
                                                reference_number: e.target.value
                                            }))}
                                            placeholder="Invoice/Reference number"
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '5px',
                                            fontWeight: 'bold',
                                            color: '#333'
                                        }}>Purchase Executive</label>
                                        <select
                                            className="form-input"
                                            value={transactionForm.executive_name}
                                            onChange={e =>
                                                setTransactionForm(prev => ({
                                                    ...prev,
                                                    executive_name: e.target.value
                                                }))
                                            }
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                fontSize: '14px'
                                            }}
                                        >
                                            <option value="">Select Executive</option>
                                            {purchaseExecutives.map((exec, idx) => (
                                                <option key={idx} value={exec}>
                                                    {exec}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '5px',
                                            fontWeight: 'bold',
                                            color: '#333'
                                        }}>Notes</label>
                                        <textarea
                                            className="form-input"
                                            rows="3"
                                            value={transactionForm.notes}
                                            onChange={e => setTransactionForm(prev => ({
                                                ...prev,
                                                notes: e.target.value
                                            }))}
                                            placeholder="Additional notes..."
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                resize: 'vertical'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer" style={{
                                padding: '20px',
                                borderTop: '1px solid #e0e0e0',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '10px'
                            }}>
                                <button
                                    className="btn btn-cancel"
                                    onClick={() => setShowTransactionModal(false)}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-save"
                                    onClick={handleSaveTransaction}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Save Purchase
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* OPENING BALANCE MODAL */}
            {
                showOpeningBalanceModal && (
                    <div className="modal-overlay" style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000
                    }}>
                        <div className="modal-content" style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            width: '90%',
                            maxWidth: '500px',
                            maxHeight: '90vh',
                            overflow: 'auto'
                        }}>
                            <div className="modal-header" style={{
                                padding: '20px',
                                borderBottom: '1px solid #e0e0e0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <h3 style={{ margin: 0 }}>Set Opening Balance</h3>
                                <button
                                    className="modal-close"
                                    onClick={() => setShowOpeningBalanceModal(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '24px',
                                        cursor: 'pointer',
                                        color: '#666'
                                    }}
                                >
                                    ×
                                </button>
                            </div>

                            <div className="modal-body" style={{ padding: '20px' }}>
                                <div className="form-group" style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '5px',
                                        fontWeight: 'bold',
                                        color: '#333'
                                    }}>Vendor</label>
                                    <input
                                        type="text"
                                        value={vendor?.name || vendor?.vendor_name || 'Unknown Vendor'}
                                        readOnly
                                        disabled
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            backgroundColor: '#f8f9fa'
                                        }}
                                    />
                                </div>

                                <div className="form-group" style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '5px',
                                        fontWeight: 'bold',
                                        color: '#333'
                                    }}>Opening Balance (₹) *</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={openingBalanceForm.opening_balance}
                                        onChange={e =>
                                            setOpeningBalanceForm(prev => ({
                                                ...prev,
                                                opening_balance: e.target.value
                                            }))
                                        }
                                        required
                                        step="0.01"
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                <div className="form-group" style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '5px',
                                        fontWeight: 'bold',
                                        color: '#333'
                                    }}>Date</label>
                                    <input
                                        type="date"
                                        className="form-input"
                                        value={openingBalanceForm.transaction_date}
                                        onChange={e =>
                                            setOpeningBalanceForm(prev => ({
                                                ...prev,
                                                transaction_date: e.target.value
                                            }))
                                        }
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                <div className="form-group" style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '5px',
                                        fontWeight: 'bold',
                                        color: '#333'
                                    }}>Notes</label>
                                    <textarea
                                        className="form-input"
                                        rows="3"
                                        value={openingBalanceForm.notes}
                                        onChange={e =>
                                            setOpeningBalanceForm(prev => ({
                                                ...prev,
                                                notes: e.target.value
                                            }))
                                        }
                                        placeholder="Optional notes about this opening balance..."
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="modal-footer" style={{
                                padding: '20px',
                                borderTop: '1px solid #e0e0e0',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '10px'
                            }}>
                                <button
                                    className="btn btn-cancel"
                                    onClick={() => setShowOpeningBalanceModal(false)}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-save"
                                    onClick={handleSaveOpeningBalance}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Save Opening Balance
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
}

export default VendorPurchaseDetails;